import * as React from 'react';
import styled from 'styled-components';
import * as _ from 'lodash/fp';

import { Dropdown } from '@process-advisor/process-miner/components/shared/dropdown';
import { SingleBlock } from '@process-advisor/process-miner/components/layout/singleBlock';
import { ActivityGrid } from '@process-advisor/process-miner/filters/path/activityGrid';
import { Button } from '@process-advisor/process-miner/components/shared/button';
import { Section } from '@process-advisor/process-miner/slicers/section';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { getShadow, getZIndex } from '@process-advisor/process-miner/shared/theme';
import { NumberInput } from '@process-advisor/process-miner/modals/numberInput';
import { AlertMessage } from '@process-advisor/process-miner/components/alerts/alert';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

import {
    getColor,
    getFontFamily,
    getFontSize,
    getLetterSpacing,
    getLineHeight,
} from '@process-advisor/process-miner/shared/theme';

const RadioComponent = styled(Radio)<{ checked?: boolean }>`
    color: ${({ checked }) =>
        checked
            ? getColor('lightBlue')
            : getColor('lightBorderColor')};

    & .MuiSvgIcon-root {
        width: 17px;
        height: 17px;
    }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
    margin-right: 30px;

    & .MuiFormControlLabel-label {
        color: ${getColor('inputTextColor')};
        font-family: ${getFontFamily('atlassian')};
        font-size: ${getFontSize('smaller')};
        letter-spacing: ${getLetterSpacing('small')};
        line-height: ${getLineHeight('small')};
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    box-shadow: ${getShadow('pane')};
    z-index: ${getZIndex('panel')};
`;

const GridOptions = styled.div`
    width: 100%;
`;

const RowRetention = styled.div`
  display: grid;
  grid-template-columns: 100px 200px;
`;

const RowConstraints= styled.div`
  display: grid;
  padding-top: 5px;
  grid-template-columns: min-content; 
`;

const RowApply = styled.div`
  display: grid;
  justify-content: center;
  padding-top: 10px;
`;

const ColumnRetentionLabel = styled.div`
  grid-column: 1 / 2 ;
`;

const ColumnRetentionValue = styled.div`
  grid-column: 2 / 2 ;
`;


const ConstraintTypeWrapper = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 90px 130px;
    grid-gap: 10px;
    grid-row: 1/1;
`;

const DurationWrapper = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 60px 140px 50px;
    grid-gap: 10px;
    grid-row: 1/1;
`;

const DurationWrapper1 = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 80px 80px;
    grid-gap: 10px;
    padding: 0px 0px 0px 0px;
`;

const DurationWrapper2 = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 80px 80px 80px;
    grid-gap: 10px;
    padding: 0px 0px 0px 0px;
`;


const optionsPath = [
    { value: 'Directly Followed', label: 'Directly Followed' },
    { value: 'Eventually Followed', label: 'Eventually Followed' },
];

const constraintTypeValues = [
    { value: 'na', label: 'N/A' },
    { value: 'duration', label: 'Duration' },
   
];
//  { value: 'same_resource', label: 'Same resource' },

const operators = [
    { value: 'greater than', label: 'Greater than' },
    { value: 'greater than or is', label: 'Greater than or is' },
    { value: 'less than', label: 'Less than' },
    { value: 'less than or is', label: 'Less than or is' },
    { value: 'equal to', label: 'Equal to' },
    { value: 'not equal to', label: 'Not equal to' },
    { value: 'between', label: 'Between' },
    { value: 'between or is', label: 'Between or is' },
    { value: 'outside', label: 'Outside' },
    { value: 'outside or is', label: 'Outside or is' },
];

const operatorValues = [
    { value: 'secs', label: 'secs' },
    { value: 'mins', label: 'mins' },
    { value: 'hrs', label: 'hrs' },
    { value: 'days', label: 'days' },
];

export const PathFilterTab = (mode) => {
    const [
        applyEnabled,
        setApplyEnabled,
    ] = React.useState<{ from: boolean, to: boolean }>({ from: false, to: false });

    const sleep = ms => new Promise(r => setTimeout(r, ms));

    const [predicate, setPredicate] = React.useState<string>('Directly Followed');
    const [constraintType, setConstraintType] = React.useState<string>('na');
    const [durationOperator, setDurationOperator] = React.useState<string>('greater than');
    const [durationActualFrom, setDurationActualFrom] = React.useState<number>(0);
    const [durationActualTo, setDurationActualTo] = React.useState<number>(0);
    const [durationValue, setDurationValue] = React.useState<string>('hrs');

    const onPathChange = (attribute: string) => {
        setPredicate(attribute);
    };

    const [retention, setRetention] = React.useState('include');

    const handleRetentionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectAction = (event.target as HTMLInputElement).value;
        setRetention(selectAction);
        apiProvider.changePathSelectRetention({ mode: selectAction });
    };

    const pane = 'Path';
    async function onApply () {
        await openProgressModal({status: 'Processing Path Constraint'})
        await sleep(50)
        await apiProvider.applyPathFilter({
            predicate: predicate,
            constraint_type: constraintType,
            duration_operator: durationOperator, 
            actual_from: durationActualFrom, 
            actual_to: durationActualTo, 
            value: durationValue, 
            mode: mode.mode})        

        setApplyEnabled({ from: false, to: false })
        closeProgressModal()
    };
    
    const onConstraintTypeChange = (type: string) => {
        setConstraintType(type);
    };

    const onDurationValueChange = (value: string) => {
        setDurationValue(value);
    };

    const onDurationOperatorChange = (operator: string) => {
        setDurationOperator(operator);
    };

    const setApplyEnabledHandler = _.curry((key: string, value: boolean) =>
        setApplyEnabled((prevState) => ({ ...prevState, [key]: value })));

    return (
        <Wrapper>
            <SingleBlock width="35%">
                <ActivityGrid
                    type="from"
                    setApplyEnabled={setApplyEnabledHandler('from')}
                    attribute={predicate}
                    selectionMode='multiple'/>
            </SingleBlock>
            <SingleBlock width="30%">
                <Dropdown
                    options={optionsPath}
                    onChange={onPathChange}
                    value={predicate}/>
            </SingleBlock>
            <SingleBlock width="35%">
                <ActivityGrid
                    type="to"
                    setApplyEnabled={setApplyEnabledHandler('to')}
                    attribute={predicate}
                    selectionMode='multiple'/>
            </SingleBlock>
            <GridOptions>
                <RowRetention>
                    <ColumnRetentionLabel>
                        <Section title='Retention'/>
                    </ColumnRetentionLabel>
                    <ColumnRetentionValue>
                    <RadioGroup
                            row
                            aria-label="selectAction"
                            name="selectAction"
                            value={retention}
                            onChange={handleRetentionChange}>
                            <StyledFormControlLabel
                                value="include"
                                control={<RadioComponent />}
                                label="Include" />
                            <StyledFormControlLabel
                                value="exclude"
                                control={<RadioComponent />}
                                label="Exclude" />
                        </RadioGroup>
                    </ColumnRetentionValue>
                </RowRetention> 
                { retention === 'include' ?
                <RowConstraints>
                    <ConstraintTypeWrapper>
                        <Section title='Type'/>
                        <Dropdown label='' value={constraintType} onChange={onConstraintTypeChange} options={constraintTypeValues} />   
                    </ConstraintTypeWrapper>

                    { constraintType === 'duration' ?
                    <DurationWrapper>
                        <Section title='Duration'/>
                        <Dropdown label='' value={durationOperator} onChange={onDurationOperatorChange} options={operators} />   
                            { durationOperator === 'greater than' || 
                              durationOperator === 'greater than or is' ||
                              durationOperator === 'less than' ||
                              durationOperator === 'less than or is' ||
                              durationOperator === 'equal to' ||     
                              durationOperator === 'not equal to' ?     
                              <DurationWrapper1>
                                <NumberInput label='' value={durationActualFrom} min={0} onChange={setDurationActualFrom}/>
                                <Dropdown label='' value={durationValue} onChange={onDurationValueChange} options={operatorValues} />                       
                              </DurationWrapper1>
                            : '' }
                            { durationOperator === 'between' || 
                              durationOperator === 'between or is' ||
                              durationOperator === 'outside' ||
                              durationOperator === 'outside or is' ?     
                              <DurationWrapper2>
                                 <NumberInput label='' value={durationActualFrom} min={0} onChange={setDurationActualFrom}/>
                                 <NumberInput label='' value={durationActualTo} min={0} onChange={setDurationActualTo}/>
                                 <Dropdown label='' value={durationValue} onChange={onDurationValueChange} options={operatorValues} />         
                              </DurationWrapper2>
                            : '' }
                    </DurationWrapper>
                    : '' }
                </RowConstraints>
                : '' }
                <RowApply>  
                    <Button label='Apply' onClick={onApply} disabled={!(applyEnabled.from && applyEnabled.to)} />
                </RowApply>
            </GridOptions>
        <AlertMessage/>
    </Wrapper>
    );
};
